import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { finalize } from 'rxjs';
import _ from 'lodash';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { BuildPathFromCode, ImportantLocations } from '@app/models/inventory/location';
import { LotItemManufacturerLotGroup } from '@app/models/inventory/lot-item-manufacturer-lot-group';
import { ItemMaster } from '@app/models/item/item-master/item-master';
import { WorkOrder, WorkOrderFabric } from '@app/models/order/work-order';
import { FabricEntry, WorkOrderSummary, YarnRequirement } from '@app/models/order/work-order-summary';
import { DyeingColor } from '@app/models/production/dyeing/dyeing-color';
import { MatchingItemMasterCommand } from '@app/models/yarn/yarn-search';
import { LotItemProxyService } from '@app/proxies/inventory/lot-item.proxy.service';
import { YarnProxyService } from '@app/proxies/item/yarn.proxy.service';

@Component({
  selector: 'app-work-order-summary-viewer',
  templateUrl: './work-order-summary-viewer.component.html',
  styleUrl: './work-order-summary-viewer.component.scss',
})
export class WorkOrderSummaryViewerComponent implements OnInit {
  @Input() workOrder!: WorkOrder;
  @Input() colors: DyeingColor[] = [];

  public WorkOrderSummaries: WorkOrderSummary[] = [];
  public YarnRequirements: YarnRequirement[] = [];

  yarnItemMasterMapping: Record<string, ItemMaster | null> = {};

  public readonly availableInventory = signal<LotItemManufacturerLotGroup[]>([]);
  public readonly isLoading = signal(0);

  public readonly totalAvailableLotItems = computed(() => this.availableInventory().reduce((sum, group) => sum + group.lotItemCount, 0));
  public readonly totalAvailableQuantity = computed(() =>
    this.availableInventory().reduce((sum, group) => sum + group.totalNetQuantity, 0),
  );

  private readonly yarnProxyService = inject(YarnProxyService);
  private readonly lotItemProxyService = inject(LotItemProxyService);

  ngOnInit(): void {
    if (!this.workOrder) {
      throw new Error('WorkOrder input is required.');
    }
    if (!this.colors || this.colors.length === 0) {
      throw new Error('Colors input is required and cannot be empty.');
    }

    this.processWorkOrder();
    this.getWorkOrderItemMaster();
  }

  private processWorkOrder(): void {
    // Group work order fabrics by color reference
    // Handle cases where a fabric might have multiple garment details with different colors
    const fabricColorPairs: Record<string, WorkOrderFabric[]> = {};

    this.workOrder.fabrics.forEach((fabric) => {
      if (fabric.garmetDetails && fabric.garmetDetails.length > 0) {
        // Create a pair for each color in garment details
        fabric.garmetDetails.forEach((garmentDetail) => {
          if (garmentDetail.colorReference) {
            if (!fabricColorPairs[garmentDetail.colorReference]) {
              fabricColorPairs[garmentDetail.colorReference] = [];
            }
            fabricColorPairs[garmentDetail.colorReference].push(fabric);
          }
        });
      }
    });

    // Process each color group to create WorkOrderSummary
    this.WorkOrderSummaries = Object.keys(fabricColorPairs).map((colorRef) => {
      const fabricsInColor = fabricColorPairs[colorRef];
      const color = this.colors.find((c) => c.id === colorRef);
      const clientProductOrderNumber = [
        ...new Set(fabricsInColor.flatMap((f) => f.garmetDetails ?? []).map((f) => f.productOrderNumber)),
      ].join(', ');

      if (!color) {
        throw new Error(`Color with reference ${colorRef} not found.`);
      }

      const workOrderSummary: WorkOrderSummary = {
        color: color,
        clientProductOrderNumber: clientProductOrderNumber,
        fabricEntries: [],
        totalRequiredQuantity: 0,
        totalKnittingQuantity: 0,
        totalYarnQuantity: 0,
      };

      fabricsInColor.forEach((fabric) => {
        const garmentDetail = fabric.garmetDetails?.find((gd) => gd.colorReference === colorRef);
        const dyeingAndFinishingLossPercentage = fabric.fabric.finishingDetails?.dyeingAndFinishingLossPercentage;
        const knittingLossPercentage = fabric.fabric.greige?.knittingProcessLossPercentage;

        if (_.isNil(garmentDetail)) {
          throw new Error(`Garment detail with color reference ${colorRef} not found in fabric ${fabric.fabricCode}.`);
        }

        if (_.isNil(dyeingAndFinishingLossPercentage)) {
          throw new Error(`Loss percentages not defined for fabric ${fabric.fabricCode}.`);
        }

        if (_.isNil(knittingLossPercentage)) {
          throw new Error(`Loss percentages not defined for fabric ${fabric.fabricCode}.`);
        }

        const requiredQuantity = garmentDetail.quantityInPounds;
        const knittingQuantity = requiredQuantity / (1 - dyeingAndFinishingLossPercentage / 100);
        const yarnQuantity = knittingQuantity / (1 - knittingLossPercentage / 100);

        const entry: FabricEntry = {
          fabricCode: fabric.fabricCode,
          greigeCode: fabric.fabric.greige?.greigeCode,
          description: fabric.fabric.longDescription!,
          gsm: fabric.fabric.gramsPerSquareMeter ?? 0,
          width: fabric.fabric.endToEndWidth ?? 0,
          requiredQuantity: requiredQuantity,
          knittingQuantity: knittingQuantity,
          yarnQuantity: yarnQuantity,
          dyeingAndFinishingLossPercentage: dyeingAndFinishingLossPercentage,
          knittingLossPercentage: knittingLossPercentage,
        };

        // Add the entry to fabricEntries array
        workOrderSummary.fabricEntries.push(entry);

        workOrderSummary.totalRequiredQuantity += requiredQuantity;
        workOrderSummary.totalKnittingQuantity += knittingQuantity;
        workOrderSummary.totalYarnQuantity += yarnQuantity;
      });
      return workOrderSummary;
    });
  }

  getWorkOrderItemMaster(): void {
    const fabricPromises: Promise<void>[] = [];

    this.workOrder.fabrics.forEach((fabric) => {
      const command: MatchingItemMasterCommand[] = [];
      fabric.fabric?.greige?.yarnDetails.forEach((yd) => {
        const certifications =
          fabric.yarnCertificationDetails?.find((cert) => cert.yarnCode === yd.yarnCode)?.certificationReferences ?? [];
        const yarnCode = yd.yarnCode;
        command.push({
          yarnCode: yarnCode!,
          certificationReference: certifications,
        });
      });

      const promise = new Promise<void>((resolve) => {
        this.yarnProxyService.getItemMastersByYarnCode(command).subscribe({
          next: (response) => {
            const yarnCodes = command.map((c) => c.yarnCode).filter((code) => code !== undefined && code !== null);
            if (!_.isNil(response.data) && !_.isEmpty(response.data)) {
              yarnCodes.forEach((yc) => {
                this.yarnItemMasterMapping[this.buildUniqueKey(yc, fabric.fabricCode)] = response!.data![yc] || null;
              });
            } else {
              yarnCodes.forEach((yc) => {
                this.yarnItemMasterMapping[this.buildUniqueKey(yc, fabric.fabricCode)] = null;
              });
            }
            resolve();
          },
          error: (e: HttpErrorResponse) => {
            const yarnCodes = command.map((c) => c.yarnCode).filter((code) => code !== undefined && code !== null);
            if (e.status == 404) {
              GlobalMessageUtil.error('Error', `No item masters found for yarn codes in fabric ${fabric.fabricCode}.`);
            }
            yarnCodes.forEach((yc) => {
              const yarnUniqueKey = this.buildUniqueKey(yc, fabric.fabricCode);
              this.yarnItemMasterMapping[yarnUniqueKey] = null;
              console.error(`Error fetching item master for yarn code: ${yc}`);
            });
            resolve();
          },
        });
      });

      fabricPromises.push(promise);
    });

    Promise.all(fabricPromises).then(() => {
      this.buildYarnRequirements();
      this.loadAvailableInventory();
    });
  }

  buildUniqueKey(yarnCode: string, fabricCode: string): string {
    return `${fabricCode}-${yarnCode}`;
  }

  getKnittingQtyTooltip(entry: FabricEntry): string {
    return (
      `Knitting Qty = Required Qty / (1 - Dyeing & Finishing Loss %)\n\n` +
      `= ${entry.requiredQuantity.toFixed(2)} / (1 - ${entry.dyeingAndFinishingLossPercentage}%)\n` +
      `= ${entry.requiredQuantity.toFixed(2)} / ${(1 - entry.dyeingAndFinishingLossPercentage / 100).toFixed(4)}\n` +
      `= ${entry.knittingQuantity.toFixed(2)}`
    );
  }

  getYarnQtyTooltip(entry: FabricEntry): string {
    return (
      `Yarn Qty = Knitting Qty / (1 - Knitting Loss %)\n\n` +
      `= ${entry.knittingQuantity.toFixed(2)} / (1 - ${entry.knittingLossPercentage}%)\n` +
      `= ${entry.knittingQuantity.toFixed(2)} / ${(1 - entry.knittingLossPercentage / 100).toFixed(4)}\n` +
      `= ${entry.yarnQuantity.toFixed(2)}`
    );
  }

  private buildYarnRequirements(): void {
    // Temporary map to group yarn codes by itemCode
    const yarnGroupMap = new Map<
      string,
      {
        pdYarnCodes: Set<string>;
        itemCode?: string;
        description: string;
        totalQuantity: number;
        fabricQuantities: Map<string, number>;
      }
    >();

    // Process each fabric and its yarn details
    this.workOrder.fabrics.forEach((fabric) => {
      const fabricQuantities = this.calculateFabricYarnQuantity(fabric);

      fabric.fabric?.greige?.yarnDetails.forEach((yarnDetail) => {
        const yarnCode = yarnDetail.yarnCode;
        if (!yarnCode) return;

        const uniqueKey = this.buildUniqueKey(yarnCode, fabric.fabricCode);
        const itemMaster = this.yarnItemMasterMapping[uniqueKey];
        const itemCode = itemMaster?.itemCode;

        // Use itemCode as group key, or yarn code if itemCode is null
        const groupKey = itemCode ?? `__null_${yarnCode}`;

        let group = yarnGroupMap.get(groupKey);
        if (!group) {
          group = {
            pdYarnCodes: new Set<string>(),
            itemCode: itemCode,
            description: itemMaster?.itemDisplayName?.en ?? '',
            totalQuantity: 0,
            fabricQuantities: new Map<string, number>(),
          };
          yarnGroupMap.set(groupKey, group);
        }

        // Add yarn code to the group
        group.pdYarnCodes.add(yarnCode);

        // Calculate quantity for this yarn in this fabric
        const yarnPercentage = (yarnDetail.usePercentage ?? 0) / 100;
        const yarnQuantity = fabricQuantities * yarnPercentage;

        // Add to total quantity
        group.totalQuantity += yarnQuantity;

        // Add to fabric quantities
        const fabricDescription = `${fabric.fabricCode} - ${fabric.fabric.longDescription ?? ''}`;
        const currentFabricQty = group.fabricQuantities.get(fabricDescription) ?? 0;
        group.fabricQuantities.set(fabricDescription, currentFabricQty + yarnQuantity);
      });
    });

    // Convert map to YarnRequirement array
    const groups = Array.from(yarnGroupMap.values());
    const workOrderTotalQuantity = groups.reduce((sum, group) => sum + group.totalQuantity, 0);

    this.YarnRequirements = groups.map((group) => ({
      pdYarnCodes: Array.from(group.pdYarnCodes),
      itemCode: group.itemCode,
      description: group.description,
      totalQuantity: group.totalQuantity,
      percentageOfTotalQuantity: workOrderTotalQuantity > 0 ? (group.totalQuantity / workOrderTotalQuantity) * 100 : 0,
      fabricQuantities: Array.from(group.fabricQuantities.entries()).map(([description, quantity]) => ({
        description,
        quantity,
      })),
    }));
  }

  private calculateFabricYarnQuantity(fabric: WorkOrderFabric): number {
    // Find all garment details for this fabric
    const garmentDetails = fabric.garmetDetails ?? [];

    const dyeingAndFinishingLossPercentage = fabric.fabric.finishingDetails?.dyeingAndFinishingLossPercentage ?? 0;
    const knittingLossPercentage = fabric.fabric.greige?.knittingProcessLossPercentage ?? 0;

    // Calculate total required quantity across all colors
    const totalRequiredQuantity = garmentDetails.reduce((sum, gd) => sum + (gd.quantityInPounds ?? 0), 0);

    // Apply loss percentages
    const knittingQuantity = totalRequiredQuantity / (1 - dyeingAndFinishingLossPercentage / 100);
    const yarnQuantity = knittingQuantity / (1 - knittingLossPercentage / 100);

    return yarnQuantity;
  }

  private loadAvailableInventory(): void {
    const itemCodes = [...new Set(this.YarnRequirements.map((yr) => yr.itemCode).filter((code): code is string => !!code))];

    // Yarns whose item master has not been created yet have nothing to look up in inventory.
    if (itemCodes.length === 0) {
      this.availableInventory.set([]);
      return;
    }

    this.incrementLoading();
    this.lotItemProxyService
      .searchGroupedByManufacturerLot({
        itemCodes: itemCodes,
        locationPaths: [BuildPathFromCode(ImportantLocations.RAW_MATERIAL)],
        excludeZeroBalances: true,
      })
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => this.availableInventory.set(response.data ?? []),
      });
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  getLoopLengths(fabricCode: string): string {
    const fabric = this.workOrder.fabrics.find((f) => f.fabricCode === fabricCode);
    if (!fabric?.fabric?.greige?.yarnDetails) {
      return 'N/A';
    }

    const loopLengths = fabric.fabric.greige.yarnDetails.map((yd) => yd.loopLength).filter((ll) => ll !== undefined && ll !== null);

    if (loopLengths.length === 0) {
      return 'N/A';
    }

    return `[${loopLengths.join(', ')}]`;
  }

  getNeedles(fabricCode: string): string {
    const fabric = this.workOrder.fabrics.find((f) => f.fabricCode === fabricCode);
    const machineSnapshot = fabric?.fabric?.greige?.machineInformation?.knittingMachineDetailsSnapshot;

    if (!machineSnapshot) {
      return 'N/A';
    }

    const cylinder = machineSnapshot.cylinderNeedles ?? 0;
    const dial = machineSnapshot.dialNeedles ?? 0;

    return `C: ${cylinder} | D: ${dial}`;
  }
}
